// ============================================
// PracticeSessionPage - Sesión Guiada de Práctica
// ============================================
// El estudiante recorre una secuencia de ejercicios
// con navegación automática, barra de progreso y
// resumen final.
// ============================================

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PracticeSurface } from "../components/practice/PracticeSurface";
import { usePracticeSession } from "../hooks/usePracticeSession";
import { useMetronome } from "../hooks/useMetronome";
import type { CheckResult } from "../lib/exerciseEngine";
import type { NoteName } from "../lib/guitarEngine";
import { getInstrumentConfig } from "../lib/instrumentEngine";
import { useInstrument } from "../contexts/InstrumentContext";
import { useBeginnerPathEntry } from "../hooks/useBeginnerPathEntry";
import { BeginnerPathPhaseShell } from "../components/beginnerPath/BeginnerPathPhaseShell";
import { StudioTunerControl } from "../components/StudioTunerControl";
import { BEGINNER_PATH_THEME as PATH } from "../lib/beginnerPath/beginnerPathTheme";
import { HOME_THEME } from "../lib/homeTheme";

// ─── Colores tema (legacy warm vs camino Serenity) ───
const LEGACY = {
  accent: "#7B2335",
  accentLight: "#A07840",
  textMain: "#2C2825",
  textMuted: "#8A8078",
  bgPanel: "#FFFFFF",
  bgWarm: "#FAF6F0",
  border: "#DDD5C8",
  success: "#6B8E5B",
  warning: "#D4BA7A",
  error: "#C44B4B",
};

type StepState = "pending" | "active" | "completed" | "skipped";

export const PracticeSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instrument } = useInstrument();
  const stringCount = getInstrumentConfig(instrument).strings.length;
  const { fromPath, phase } = useBeginnerPathEntry();
  const pathMode = fromPath && phase?.id === "guided-practice";

  const C = pathMode
    ? {
        accent: "#99f6e4",
        accentLight: "#7ecfc0",
        textMain: PATH.text,
        textMuted: PATH.textMuted,
        bgPanel: "rgba(255,255,255,0.04)",
        bgWarm: HOME_THEME.bg,
        border: PATH.cardBorder,
        success: "#6B8E5B",
        warning: "#D4BA7A",
        error: "#C44B4B",
      }
    : LEGACY;

  const {
    state: sessionState,
    loadSession,
    loadDefaultSession,
    loadFromTask,
    startSession,
    nextExercise,
    skipExercise,
    completeExercise,
    recordAttempt,
    resetSession,
  } = usePracticeSession();

  const metronome = useMetronome();

  // Estado local para ejercicios interactivos
  const [selectedQuizOption, setSelectedQuizOption] = useState<string | null>(null);
  const [clickedPositions, setClickedPositions] = useState<{ string: number; fret: number }[]>([]);
  const [currentSeqStep, setCurrentSeqStep] = useState(0);
  const [isSequencePlaying, setIsSequencePlaying] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Cargar sesión desde tarea al montar ───
  useEffect(() => {
    const taskData = (location.state as any)?.task;
    if (taskData && sessionState.status === "idle") {
      loadFromTask(taskData.type, taskData.config);
    } else if (sessionState.status === "idle") {
      loadDefaultSession(undefined, pathMode ? "C" : undefined);
    }
    // Solo al montar / al entrar en pathMode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathMode]);

  const exitTarget = pathMode ? "/learn/path" : "/home";

  const wrapPath = (node: React.ReactNode, opts?: { fullBleed?: boolean }) => {
    if (pathMode && phase) {
      return (
        <BeginnerPathPhaseShell phase={phase} fullBleed={opts?.fullBleed}>
          {opts?.fullBleed ? node : <div className="pb-10">{node}</div>}
        </BeginnerPathPhaseShell>
      );
    }
    return node;
  };

  const pathPanel = pathMode
    ? {
        surface: "rgba(255,255,255,0.04)",
        surfaceSoft: "rgba(255,255,255,0.06)",
        text: PATH.text,
        muted: PATH.textMuted,
        dim: PATH.textDim,
        border: PATH.cardBorder,
        chipBg: "rgba(153,246,228,0.14)",
        chipText: "#99f6e4",
        btnPrimaryBg: PATH.btnMint,
        btnPrimaryFg: "#100f0e",
        btnSecondaryBg: "rgba(255,255,255,0.1)",
        btnSecondaryFg: "#f5f5f4",
        btnSecondaryBorder: "rgba(255,255,255,0.22)",
        track: "rgba(255,255,255,0.12)",
        trackFill: "#99f6e4",
        tipBg: "rgba(251,191,36,0.1)",
        tipBorder: "rgba(251,191,36,0.28)",
        tipTitle: "#fde68a",
        tipText: "rgba(253,230,138,0.9)",
      }
    : null;

  // ─── Animación de secuencia ───
  useEffect(() => {
    if (!isSequencePlaying || !sessionState.currentExercise?.sequence) return;

    const bpm = sessionState.currentExercise.exerciseDef.bpm || 60;
    const intervalMs = (60 / bpm) * 1000;

    timerRef.current = setInterval(() => {
      setCurrentSeqStep((prev) => {
        const seq = sessionState.currentExercise?.sequence;
        if (!seq || prev >= seq.length - 1) {
          setIsSequencePlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isSequencePlaying, sessionState.currentExercise]);

  // ─── Active note secuencia ───
  const activeNote = useMemo(() => {
    if (!sessionState.currentExercise?.sequence || sessionState.currentExercise.sequence.length === 0)
      return null;
    if (currentSeqStep >= sessionState.currentExercise.sequence.length) return null;
    const step = sessionState.currentExercise.sequence[currentSeqStep];
    return { string: step.string, fret: step.fret };
  }, [sessionState.currentExercise, currentSeqStep]);

  // ─── Handlers ───

  const handleQuizAnswer = (option: string) => {
    if (!sessionState.currentExercise) return;
    const def = sessionState.currentExercise.exerciseDef;
    if (!def.checkAnswer) return;

    setSelectedQuizOption(option);
    const result = def.checkAnswer(sessionState.currentExercise.exerciseState, option);
    recordAttempt(result);

    if (result.correct) {
      completeExercise({
        completed: true,
        stars: result.stars,
        score: result.stars * 10,
        feedback: result.message,
      });
    }
  };

  const handleFretClick = (stringNum: number, fret: number, _note: NoteName) => {
    if (!sessionState.currentExercise) return;
    const def = sessionState.currentExercise.exerciseDef;
    if (!def.checkAnswer) return;

    const click = { string: stringNum, fret };
    setClickedPositions((prev) => [...prev, click]);

    const result = def.checkAnswer(sessionState.currentExercise.exerciseState, click);
    recordAttempt(result);

    if (result.correct) {
      completeExercise({
        completed: true,
        stars: result.stars,
        score: result.stars * 10,
        feedback: result.message,
      });
    }
  };

  const handlePlaySequence = () => {
    if (!sessionState.currentExercise?.sequence) return;
    setIsSequencePlaying(true);
    setCurrentSeqStep(0);
    metronome.start();
  };

  const handleStopSequence = () => {
    setIsSequencePlaying(false);
    setCurrentSeqStep(0);
    metronome.stop();
    if (timerRef.current) clearInterval(timerRef.current);

    // Completar ejercicio de secuencia
    completeExercise({
      completed: true,
      stars: 2,
      score: 20,
      feedback: "¡Secuencia completada!",
    });
  };

  // ─── Calcular estado de cada paso ───
  const getStepState = (stepId: string): StepState => {
    if (sessionState.progress?.completed[stepId]) return "completed";
    if (stepId === sessionState.currentExercise?.stepId) {
      return sessionState.exerciseStatus === "completed" ? "completed" : "active";
    }
    return "pending";
  };

  // ─── Feedback visual ───
  useEffect(() => {
    if (sessionState.feedback?.correct) {
      setShowCelebration(true);
      const t = setTimeout(() => setShowCelebration(false), 2000);
      return () => clearTimeout(t);
    }
  }, [sessionState.feedback]);

  // ─── Resumen final ───
  if (sessionState.status === "completed" && sessionState.plan) {
    const total = sessionState.plan.exercises.length;
    const completed = Object.values(sessionState.progress?.completed || {}).filter(
      (r) => r.completed
    ).length;
    const stars = sessionState.progress?.totalStars || 0;
    const score = sessionState.progress?.totalScore || 0;

    return wrapPath(
      <div
        className={pathMode ? "flex items-center justify-center p-2" : "min-h-screen flex items-center justify-center p-4"}
        style={pathMode ? undefined : { backgroundColor: C.bgWarm }}
      >
        <div
          className="w-full max-w-lg rounded-2xl shadow-lg border p-8 text-center"
          style={{
            background: pathMode ? "rgba(255,255,255,0.04)" : "#fff",
            borderColor: pathMode ? PATH.cardBorder : "#DDD5C8",
          }}
        >
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-serif font-bold mb-2" style={{ color: C.textMain }}>
            {sessionState.plan.closingTip}
          </h1>
          <p className="text-sm font-sans mb-6" style={{ color: C.textMuted }}>
            Completaste tu sesión de práctica
          </p>

          {/* Estadísticas */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-3 rounded-lg bg-[#FAF6F0] border border-[#E8E0D4]">
              <div className="text-2xl font-bold text-[#7B2335]">{completed}/{total}</div>
              <div className="text-[10px] text-[#8A8078] font-sans uppercase tracking-wider">
                Ejercicios
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[#FAF6F0] border border-[#E8E0D4]">
              <div className="text-2xl font-bold text-[#D4BA7A]">{stars} ⭐</div>
              <div className="text-[10px] text-[#8A8078] font-sans uppercase tracking-wider">
                Estrellas
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[#FAF6F0] border border-[#E8E0D4]">
              <div className="text-2xl font-bold text-[#6B8E5B]">{score}</div>
              <div className="text-[10px] text-[#8A8078] font-sans uppercase tracking-wider">
                Puntos
              </div>
            </div>
          </div>

          {/* Lista de ejercicios */}
          <div className="text-left mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#8A8078] font-sans mb-2">
              Detalle
            </h3>
            <div className="space-y-1.5">
              {sessionState.plan.exercises.map((ex, i) => {
                const result = sessionState.progress?.completed[ex.stepId];
                return (
                  <div
                    key={ex.stepId}
                    className={`flex items-center gap-2 p-2 rounded-lg text-xs font-sans ${
                      result?.completed
                        ? "bg-[#6B8E5B]/10 text-[#6B8E5B]"
                        : result
                        ? "bg-[#C44B4B]/10 text-[#C44B4B]"
                        : "bg-[#F5F0E8] text-[#8A8078]"
                    }`}
                  >
                    <span className="text-base">{ex.title.split(" ")[0]}</span>
                    <span className="flex-1 truncate">{ex.title}</span>
                    <span>
                      {result?.completed ? "✅" : result ? "⏭️" : "⏳"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Botones */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                resetSession();
                navigate(exitTarget);
              }}
              className="flex-1 py-3 rounded-lg text-sm font-sans font-bold transition-all"
              style={{
                background: pathMode ? "rgba(255,255,255,0.06)" : "#F5F0E8",
                color: pathMode ? C.textMuted : "#5A524A",
              }}
            >
              {pathMode ? "← Camino" : "🏠 Ir al inicio"}
            </button>
            <button
              onClick={() => {
                resetSession();
                loadDefaultSession(undefined, pathMode ? "C" : undefined);
              }}
              className="flex-1 py-3 rounded-lg text-sm font-sans font-bold transition-all"
              style={{
                background: pathMode ? PATH.btnMint : C.accent,
                color: pathMode ? "#100f0e" : "#fff",
              }}
            >
              🔄 Nueva sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Pantalla de inicio de sesión ───
  if (sessionState.status === "ready" && sessionState.plan) {
    const totalMinutes = sessionState.plan.totalMinutes;
    const exercisesCount = sessionState.plan.exercises.length;

    return wrapPath(
      <div
        className={pathMode ? "flex items-center justify-center p-2" : "min-h-screen flex items-center justify-center p-4"}
        style={pathMode ? undefined : { backgroundColor: C.bgWarm }}
      >
        <div
          className="w-full max-w-lg rounded-2xl shadow-lg border p-8 text-center"
          style={{
            background: pathMode ? "rgba(255,255,255,0.04)" : "#fff",
            borderColor: pathMode ? PATH.cardBorder : "#DDD5C8",
          }}
        >
          <div className="text-6xl mb-4">{sessionState.plan.emoji}</div>
          <h1 className="text-2xl font-serif font-bold mb-2" style={{ color: C.textMain }}>
            {sessionState.plan.title}
          </h1>
          <p className="text-sm font-sans mb-6" style={{ color: C.textMuted }}>
            {sessionState.plan.description}
          </p>

          {/* Info de la sesión */}
          <div className="flex justify-center gap-6 mb-6">
            <div className="text-center">
              <div className="text-xl font-bold text-[#7B2335]">{exercisesCount}</div>
              <div className="text-[10px] text-[#8A8078] font-sans uppercase tracking-wider">
                Ejercicios
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#7B2335]">~{totalMinutes} min</div>
              <div className="text-[10px] text-[#8A8078] font-sans uppercase tracking-wider">
                Duración
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#7B2335]">
                {sessionState.plan.level === "principiante" ? "🌱" : sessionState.plan.level === "intermedio" ? "🌿" : "🌳"}
              </div>
              <div className="text-[10px] text-[#8A8078] font-sans uppercase tracking-wider">
                Nivel
              </div>
            </div>
          </div>

          {/* Lista de ejercicios */}
          <div className="text-left mb-6">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#8A8078] font-sans mb-2">
              Tu plan hoy
            </h3>
            <div className="space-y-2">
              {sessionState.plan.exercises.map((ex, i) => (
                <div
                  key={ex.stepId}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-[#FAF6F0] border border-[#E8E0D4]"
                >
                  <span className="w-6 h-6 rounded-full bg-[#7B2335] text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-sans font-semibold text-[#2C2825]">
                      {ex.title}
                    </div>
                    <div className="text-[10px] text-[#8A8078] font-sans">
                      {ex.goal}
                    </div>
                  </div>
                  <span className="text-[10px] text-[#8A8078] font-sans shrink-0">
                    ~{Math.ceil(ex.estimatedDurationSec / 60)} min
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={startSession}
            className="w-full py-3.5 rounded-lg text-base font-sans font-bold transition-all shadow-md"
            style={{
              background: pathMode ? PATH.btnMint : C.accent,
              color: pathMode ? "#100f0e" : "#fff",
            }}
          >
            🚀 ¡Empezar práctica!
          </button>

          <button
            type="button"
            onClick={() => { resetSession(); navigate(pathMode ? "/learn/path" : -1); }}
            className="mt-3 text-xs font-sans transition-colors"
            style={{ color: C.textMuted }}
          >
            ← Volver
          </button>
        </div>
      </div>
    );
  }

  // ─── Ejercicio activo ───
  if (
    (sessionState.status === "in-progress" || sessionState.status === "paused") &&
    sessionState.currentExercise &&
    sessionState.plan
  ) {
    const exercise = sessionState.currentExercise;
    const totalExercises = sessionState.plan.exercises.length;
    const stepState = getStepState(exercise.stepId);
    const isCompleted = stepState === "completed";
    const P = pathPanel;

    return wrapPath(
      <div
        className={
          pathMode
            ? "flex h-full min-h-0 flex-col"
            : "min-h-screen flex flex-col"
        }
        style={{ backgroundColor: pathMode ? "transparent" : C.bgWarm }}
      >
        {/* ─── Barra Superior ─── */}
        <div
          className={pathMode ? "shrink-0 px-3 py-3 rounded-2xl border mb-2" : "px-4 py-3 border-b"}
          style={{
            backgroundColor: pathMode ? P!.surface : C.bgPanel,
            borderColor: pathMode ? P!.border : C.border,
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <button
                type="button"
                onClick={() => {
                  resetSession();
                  navigate(exitTarget);
                }}
                className="transition-colors shrink-0"
                style={{ color: pathMode ? P!.muted : C.textMuted }}
                aria-label="Salir"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider font-sans truncate"
                    style={{ color: pathMode ? P!.dim : "#8A8078" }}
                  >
                    {sessionState.plan.title}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <StudioTunerControl compact />
                    <span
                      className="text-[10px] font-sans tabular-nums"
                      style={{ color: pathMode ? P!.muted : "#8A8078" }}
                    >
                      {sessionState.currentIndex + 1} de {totalExercises}
                    </span>
                  </div>
                </div>
                <div
                  className="w-full h-1.5 rounded-full overflow-hidden"
                  style={{ background: pathMode ? P!.track : "#E8E0D4" }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(sessionState.currentIndex / totalExercises) * 100}%`,
                      background: pathMode ? P!.trackFill : "#7B2335",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-1.5">
              {sessionState.plan.exercises.map((ex, i) => {
                const st = getStepState(ex.stepId);
                return (
                  <div
                    key={ex.stepId}
                    className="flex-1 h-1 rounded-full transition-all"
                    style={{
                      background:
                        st === "completed"
                          ? "#6B8E5B"
                          : st === "active"
                            ? pathMode
                              ? "#99f6e4"
                              : "#7B2335"
                            : pathMode
                              ? "rgba(255,255,255,0.12)"
                              : "#E8E0D4",
                    }}
                    title={`${i + 1}. ${ex.title}`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* ─── Body ─── */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-2 overflow-hidden">
          {/* ─── Área central: mástil ─── */}
          <div
            className={
              pathMode
                ? "flex-1 flex flex-col min-h-0 rounded-2xl border overflow-hidden"
                : "flex-1 flex flex-col min-h-0"
            }
            style={
              pathMode
                ? { backgroundColor: P!.surface, borderColor: P!.border }
                : undefined
            }
          >
            <div className="flex-1 min-h-0 flex items-center justify-center p-3 sm:p-4 overflow-x-auto">
              <div className="w-full h-full max-w-5xl min-h-[min(42vh,420px)]">
                <PracticeSurface
                  highlightedPositions={exercise.exerciseState.neckPositions}
                  activeNote={activeNote}
                  onFretClick={
                    exercise.exerciseDef.type === "click" && !isCompleted
                      ? handleFretClick
                      : undefined
                  }
                  showNoteNames={true}
                  showDegrees={true}
                  interactive={exercise.exerciseDef.type === "click" && !isCompleted}
                  stringOrder="physical"
                  showFingerBadges={true}
                  hand="left"
                  hideFretZero={false}
                  leftHanded={false}
                />
              </div>
            </div>

            {/* Controles inferiores */}
            <div
              className="shrink-0 px-4 py-2.5 border-t"
              style={{
                backgroundColor: pathMode ? "rgba(0,0,0,0.2)" : C.bgPanel,
                borderColor: pathMode ? P!.border : "#E8E0D4",
              }}
            >
              <div className="max-w-5xl mx-auto flex items-center gap-3 flex-wrap">
                {exercise.sequence && !isCompleted && (
                  <>
                    <button
                      type="button"
                      onClick={isSequencePlaying ? handleStopSequence : handlePlaySequence}
                      className="px-3 h-9 rounded-lg text-xs font-sans font-bold transition-all"
                      style={
                        isSequencePlaying
                          ? { background: "#C44B4B", color: "#fff" }
                          : pathMode
                            ? { background: P!.btnPrimaryBg, color: P!.btnPrimaryFg }
                            : { background: "#7B2335", color: "#fff" }
                      }
                    >
                      {isSequencePlaying ? "⏸ Pausa" : "▶ Recorrer"}
                    </button>
                    {isSequencePlaying && (
                      <div className="flex-1 flex items-center gap-2 min-w-[120px]">
                        <div
                          className="flex-1 h-1.5 rounded-full overflow-hidden"
                          style={{ background: pathMode ? P!.track : "#E8E0D4" }}
                        >
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${((currentSeqStep + 1) / (exercise.sequence?.length || 1)) * 100}%`,
                              background: pathMode ? P!.trackFill : "#7B2335",
                            }}
                          />
                        </div>
                        <span
                          className="text-[10px] font-sans tabular-nums"
                          style={{ color: pathMode ? P!.muted : "#8A8078" }}
                        >
                          {currentSeqStep + 1}/{exercise.sequence?.length}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="flex items-center gap-3 ml-auto">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#D45D79] inline-block" />
                    <span
                      className="text-[9px] font-sans"
                      style={{ color: pathMode ? P!.dim : "#5A524A" }}
                    >
                      Tónica
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#5BA8A8] inline-block" />
                    <span
                      className="text-[9px] font-sans"
                      style={{ color: pathMode ? P!.dim : "#5A524A" }}
                    >
                      3ª
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#8FB069] inline-block" />
                    <span
                      className="text-[9px] font-sans"
                      style={{ color: pathMode ? P!.dim : "#5A524A" }}
                    >
                      5ª
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Panel lateral derecho (inspector) ─── */}
          <div
            className={
              pathMode
                ? "w-full lg:w-[min(100%,320px)] shrink-0 rounded-2xl border overflow-y-auto max-h-[42vh] lg:max-h-none"
                : "w-full lg:w-80 border-t lg:border-t-0 lg:border-l overflow-y-auto"
            }
            style={{
              backgroundColor: pathMode ? P!.surface : C.bgPanel,
              borderColor: pathMode ? P!.border : "#E8E0D4",
            }}
          >
            <div className="p-4 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{exercise.title.split(" ")[0]}</span>
                  <h3
                    className="text-sm font-sans font-bold"
                    style={{ color: pathMode ? P!.text : "#2C2825" }}
                  >
                    {exercise.title}
                  </h3>
                </div>
                <p className="text-[10px] font-sans" style={{ color: pathMode ? P!.muted : "#8A8078" }}>
                  {exercise.description}
                </p>
              </div>

              <div
                className="p-2.5 rounded-lg border"
                style={{
                  background: pathMode ? P!.surfaceSoft : "#FAF6F0",
                  borderColor: pathMode ? P!.border : "#E8E0D4",
                }}
              >
                <div
                  className="text-[10px] font-semibold uppercase tracking-wider font-sans mb-1"
                  style={{ color: pathMode ? P!.dim : "#8A8078" }}
                >
                  Objetivo
                </div>
                <p className="text-xs font-sans" style={{ color: pathMode ? P!.text : "#2C2825" }}>
                  {exercise.goal}
                </p>
              </div>

              <div>
                <h4
                  className="text-[10px] font-semibold uppercase tracking-wider font-sans mb-2"
                  style={{ color: pathMode ? P!.dim : "#8A8078" }}
                >
                  Instrucciones
                </h4>
                <div className="space-y-1.5">
                  {exercise.exerciseState.instructions.map((inst, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 p-2 rounded text-[11px] font-sans"
                      style={{
                        background: pathMode ? P!.surfaceSoft : "#FAF6F0",
                        color: pathMode ? P!.text : "#2C2825",
                      }}
                    >
                      <span
                        className="shrink-0 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center"
                        style={{
                          background: pathMode ? P!.chipBg : "#7B2335",
                          color: pathMode ? P!.chipText : "#fff",
                        }}
                      >
                        {i + 1}
                      </span>
                      <span>{inst}</span>
                    </div>
                  ))}
                </div>
              </div>

              {exercise.exerciseState.quizOptions && !isCompleted && (
                <div>
                  <h4
                    className="text-[10px] font-semibold uppercase tracking-wider font-sans mb-2"
                    style={{ color: pathMode ? P!.dim : "#8A8078" }}
                  >
                    Tu respuesta
                  </h4>
                  <div className="space-y-1.5">
                    {exercise.exerciseState.quizOptions.map((opt) => {
                      const isSelected = selectedQuizOption === opt;
                      const fb = sessionState.feedback;
                      const isCorrect = fb?.correct && isSelected;
                      const isWrong = !fb?.correct && isSelected;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleQuizAnswer(opt)}
                          disabled={!!fb?.correct}
                          className="w-full p-2.5 rounded-lg text-sm font-sans font-semibold transition-all border text-left"
                          style={
                            isCorrect
                              ? {
                                  background: "rgba(107,142,91,0.15)",
                                  borderColor: "#6B8E5B",
                                  color: "#86efac",
                                }
                              : isWrong
                                ? {
                                    background: "rgba(196,75,75,0.15)",
                                    borderColor: "#C44B4B",
                                    color: "#fca5a5",
                                  }
                                : isSelected
                                  ? pathMode
                                    ? {
                                        background: "rgba(153,246,228,0.15)",
                                        borderColor: "#99f6e4",
                                        color: "#99f6e4",
                                      }
                                    : {
                                        background: "rgba(123,35,53,0.1)",
                                        borderColor: "#7B2335",
                                        color: "#7B2335",
                                      }
                                  : pathMode
                                    ? {
                                        background: "rgba(255,255,255,0.06)",
                                        borderColor: P!.border,
                                        color: P!.text,
                                      }
                                    : {
                                        background: "#fff",
                                        borderColor: "#E8E0D4",
                                        color: "#2C2825",
                                      }
                          }
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {exercise.exerciseDef.type === "click" && !isCompleted && (
                <div
                  className="p-2.5 rounded-lg border"
                  style={{
                    background: pathMode ? P!.surfaceSoft : "#FAF6F0",
                    borderColor: pathMode ? P!.border : "#E8E0D4",
                  }}
                >
                  <div
                    className="text-[10px] font-semibold uppercase tracking-wider font-sans mb-1"
                    style={{ color: pathMode ? P!.dim : "#8A8078" }}
                  >
                    Tu misión
                  </div>
                  <div
                    className="text-sm font-sans font-semibold"
                    style={{ color: pathMode ? "#99f6e4" : "#7B2335" }}
                  >
                    {exercise.exerciseState.targetNote}
                  </div>
                  <div
                    className="text-[10px] font-sans mt-1"
                    style={{ color: pathMode ? P!.muted : "#8A8078" }}
                  >
                    Haz click en el mástil cuando encuentres la nota
                  </div>
                  {clickedPositions.length > 0 && (
                    <div
                      className="mt-1 text-[10px] font-sans"
                      style={{ color: pathMode ? P!.dim : "#5A524A" }}
                    >
                      Tocaste {clickedPositions.length} nota(s)
                    </div>
                  )}
                </div>
              )}

              {sessionState.feedback && (
                <div
                  className="p-3 rounded-lg border text-center"
                  style={
                    sessionState.feedback.correct
                      ? {
                          background: "rgba(107,142,91,0.12)",
                          borderColor: "#6B8E5B",
                        }
                      : {
                          background: "rgba(196,75,75,0.12)",
                          borderColor: "#C44B4B",
                        }
                  }
                >
                  <div className="text-2xl mb-1">{sessionState.feedback.emoji}</div>
                  <div
                    className="text-xs font-sans font-semibold"
                    style={{
                      color: sessionState.feedback.correct
                        ? pathMode
                          ? "#86efac"
                          : "#6B8E5B"
                        : pathMode
                          ? "#fca5a5"
                          : "#C44B4B",
                    }}
                  >
                    {sessionState.feedback.message}
                  </div>
                </div>
              )}

              <div
                className="p-2.5 rounded-lg border"
                style={{
                  background: pathMode ? P!.tipBg : "#FFF8E1",
                  borderColor: pathMode ? P!.tipBorder : "#F0E0B0",
                }}
              >
                <div
                  className="text-[10px] font-semibold uppercase tracking-wider font-sans mb-1"
                  style={{ color: pathMode ? P!.tipTitle : "#A07840" }}
                >
                  Pista
                </div>
                <p
                  className="text-[11px] font-sans"
                  style={{ color: pathMode ? P!.tipText : "#5A4A2A" }}
                >
                  {exercise.tip}
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                {!isCompleted ? (
                  <button
                    type="button"
                    onClick={skipExercise}
                    className="flex-1 py-2.5 rounded-lg text-xs font-sans font-bold transition-all border"
                    style={
                      pathMode
                        ? {
                            background: P!.btnSecondaryBg,
                            color: P!.btnSecondaryFg,
                            borderColor: P!.btnSecondaryBorder,
                          }
                        : {
                            background: "#F5F0E8",
                            color: "#5A524A",
                            borderColor: "transparent",
                          }
                    }
                  >
                    ⏭ Saltar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={nextExercise}
                    className="flex-1 py-2.5 rounded-lg text-xs font-sans font-bold transition-all border border-transparent"
                    style={
                      pathMode
                        ? { background: P!.btnPrimaryBg, color: P!.btnPrimaryFg }
                        : { background: "#7B2335", color: "#fff" }
                    }
                  >
                    {sessionState.currentIndex + 1 >= (sessionState.plan?.exercises.length || 0)
                      ? "🎉 Ver resultados"
                      : "➡️ Siguiente"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {showCelebration && (
          <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-50">
            <div
              className="rounded-2xl px-8 py-6 shadow-2xl border text-center animate-bounce"
              style={{
                background: pathMode ? "rgba(16,15,14,0.92)" : "rgba(255,255,255,0.9)",
                borderColor: pathMode ? P!.border : "#DDD5C8",
                color: pathMode ? "#86efac" : "#6B8E5B",
              }}
            >
              <div className="text-5xl mb-2">🌟</div>
              <div className="text-lg font-bold font-sans">¡Correcto!</div>
            </div>
          </div>
        )}
      </div>,
      { fullBleed: pathMode },
    );
  }

  // ─── Cargando / idle ───
  return wrapPath(
    <div
      className={pathMode ? "flex items-center justify-center py-16" : "min-h-screen flex items-center justify-center"}
      style={pathMode ? undefined : { backgroundColor: C.bgWarm }}
    >
      <div className="text-center">
        <div className="text-4xl mb-4 animate-pulse">🎸</div>
        <p className="text-sm font-sans" style={{ color: C.textMuted }}>
          Preparando tu sesión de práctica...
        </p>
      </div>
    </div>
  );
};

export default PracticeSessionPage;
